import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTenant } from '../../contexts/TenantContext';
import { productionApi } from '../../api/production.api';
import type { PlantFamily, Variety, SeedLot } from '../../types/production.types';

const SEED_SOURCE_LABELS: Record<string, string> = {
  PURCHASED: 'Achetee',
  OWN_HARVEST: 'Recolte propre',
  EXCHANGE: 'Echange',
  GIFTED: 'Don',
};

export default function VarietiesAndSeeds() {
  const { t } = useTranslation();
  const { tenant, loading: tenantLoading } = useTenant();

  const [tab, setTab] = useState<'varieties' | 'seeds'>('varieties');
  const [families, setFamilies] = useState<PlantFamily[]>([]);
  const [varieties, setVarieties] = useState<Variety[]>([]);
  const [seedLots, setSeedLots] = useState<SeedLot[]>([]);
  const [familyFilter, setFamilyFilter] = useState('');
  const [loading, setLoading] = useState(true);

  // Variety modal
  const [varietyModalOpen, setVarietyModalOpen] = useState(false);
  const [editingVariety, setEditingVariety] = useState<Variety | null>(null);
  const [varietyForm, setVarietyForm] = useState({
    plant_family_id: '', name: '', species: '', is_hybrid: false, needs_tunnel: false,
    seeding_start_week: '', seeding_end_week: '', transplant_week_offset: '', days_to_maturity: '',
    harvest_duration_weeks: '', spacing_row_cm: '', spacing_plant_cm: '', notes: '',
  });

  // SeedLot modal
  const [seedModalOpen, setSeedModalOpen] = useState(false);
  const [seedForm, setSeedForm] = useState({
    variety_id: '', source: 'PURCHASED', supplier_name: '', lot_number: '',
    initial_quantity: '', unit: 'graines', price_total_cents: '', germination_rate: '',
    is_certified_bio: true, certification: '', notes: '',
  });

  useEffect(() => {
    if (!tenantLoading && tenant) loadData();
  }, [tenant, tenantLoading]);

  useEffect(() => {
    if (!tenantLoading && tenant) {
      if (tab === 'varieties') loadVarieties();
      else loadSeedLots();
    }
  }, [tab, familyFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [fam, vars, lots] = await Promise.all([
        productionApi.listPlantFamilies(),
        productionApi.listVarieties(),
        productionApi.listSeedLots(),
      ]);
      setFamilies(fam);
      setVarieties(vars);
      setSeedLots(lots);
    } catch (err: any) {
      alert('Erreur: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadVarieties = async () => {
    try {
      const data = await productionApi.listVarieties(familyFilter || undefined);
      setVarieties(data);
    } catch (err: any) {
      console.error(err);
    }
  };

  const loadSeedLots = async () => {
    try {
      const data = await productionApi.listSeedLots();
      setSeedLots(data);
    } catch (err: any) {
      console.error(err);
    }
  };

  const openVarietyModal = (v?: Variety) => {
    if (v) {
      setEditingVariety(v);
      setVarietyForm({
        plant_family_id: v.plant_family_id, name: v.name, species: v.species,
        is_hybrid: v.is_hybrid, needs_tunnel: v.needs_tunnel,
        seeding_start_week: v.seeding_start_week?.toString() || '',
        seeding_end_week: v.seeding_end_week?.toString() || '',
        transplant_week_offset: v.transplant_week_offset?.toString() || '',
        days_to_maturity: v.days_to_maturity?.toString() || '',
        harvest_duration_weeks: v.harvest_duration_weeks?.toString() || '',
        spacing_row_cm: v.spacing_row_cm?.toString() || '',
        spacing_plant_cm: v.spacing_plant_cm?.toString() || '',
        notes: v.notes || '',
      });
    } else {
      setEditingVariety(null);
      setVarietyForm({
        plant_family_id: '', name: '', species: '', is_hybrid: false, needs_tunnel: false,
        seeding_start_week: '', seeding_end_week: '', transplant_week_offset: '', days_to_maturity: '',
        harvest_duration_weeks: '', spacing_row_cm: '', spacing_plant_cm: '', notes: '',
      });
    }
    setVarietyModalOpen(true);
  };

  const saveVariety = async () => {
    try {
      const data: any = {
        plant_family_id: varietyForm.plant_family_id, name: varietyForm.name,
        species: varietyForm.species, is_hybrid: varietyForm.is_hybrid, needs_tunnel: varietyForm.needs_tunnel,
        notes: varietyForm.notes || null,
      };
      if (varietyForm.seeding_start_week) data.seeding_start_week = parseInt(varietyForm.seeding_start_week);
      if (varietyForm.seeding_end_week) data.seeding_end_week = parseInt(varietyForm.seeding_end_week);
      if (varietyForm.transplant_week_offset) data.transplant_week_offset = parseInt(varietyForm.transplant_week_offset);
      if (varietyForm.days_to_maturity) data.days_to_maturity = parseInt(varietyForm.days_to_maturity);
      if (varietyForm.harvest_duration_weeks) data.harvest_duration_weeks = parseInt(varietyForm.harvest_duration_weeks);
      if (varietyForm.spacing_row_cm) data.spacing_row_cm = parseInt(varietyForm.spacing_row_cm);
      if (varietyForm.spacing_plant_cm) data.spacing_plant_cm = parseInt(varietyForm.spacing_plant_cm);

      if (editingVariety) {
        await productionApi.updateVariety(editingVariety.id, data);
      } else {
        await productionApi.createVariety(data);
      }
      setVarietyModalOpen(false);
      loadVarieties();
    } catch (err: any) {
      alert('Erreur: ' + err.message);
    }
  };

  const saveSeedLot = async () => {
    try {
      const data: any = {
        variety_id: seedForm.variety_id, source: seedForm.source,
        supplier_name: seedForm.supplier_name || null, lot_number: seedForm.lot_number || null,
        initial_quantity: parseFloat(seedForm.initial_quantity), unit: seedForm.unit,
        is_certified_bio: seedForm.is_certified_bio, certification: seedForm.certification || null,
        notes: seedForm.notes || null,
      };
      if (seedForm.price_total_cents) data.price_total_cents = Math.round(parseFloat(seedForm.price_total_cents) * 100);
      if (seedForm.germination_rate) data.germination_rate = parseInt(seedForm.germination_rate);

      await productionApi.createSeedLot(data);
      setSeedModalOpen(false);
      loadSeedLots();
    } catch (err: any) {
      alert('Erreur: ' + err.message);
    }
  };

  // Group varieties by species
  const speciesGroups = varieties.reduce((acc, v) => {
    const sp = v.species;
    if (!acc[sp]) acc[sp] = [];
    acc[sp].push(v);
    return acc;
  }, {} as Record<string, Variety[]>);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">
          {t('production.varieties.title', 'Varietes & Semences')}
        </h1>
        <div className="flex gap-2">
          {tab === 'varieties' && (
            <button onClick={() => openVarietyModal()} className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 text-sm">
              + Nouvelle variete
            </button>
          )}
          {tab === 'seeds' && (
            <button onClick={() => { setSeedForm({ variety_id: '', source: 'PURCHASED', supplier_name: '', lot_number: '', initial_quantity: '', unit: 'graines', price_total_cents: '', germination_rate: '', is_certified_bio: true, certification: '', notes: '' }); setSeedModalOpen(true); }} className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 text-sm">
              + Nouveau lot
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
        <button onClick={() => setTab('varieties')} className={`px-4 py-2 text-sm rounded-md ${tab === 'varieties' ? 'bg-white shadow font-medium' : 'text-gray-600'}`}>
          Varietes ({varieties.length})
        </button>
        <button onClick={() => setTab('seeds')} className={`px-4 py-2 text-sm rounded-md ${tab === 'seeds' ? 'bg-white shadow font-medium' : 'text-gray-600'}`}>
          Stock semences ({seedLots.length})
        </button>
      </div>

      {/* Filter */}
      {tab === 'varieties' && (
        <select className="input w-64" value={familyFilter} onChange={(e) => setFamilyFilter(e.target.value)}>
          <option value="">Toutes les familles</option>
          {families.map((f) => (
            <option key={f.id} value={f.id}>{f.name} ({f.latin_name})</option>
          ))}
        </select>
      )}

      {/* Varieties Tab */}
      {tab === 'varieties' && (
        <div className="space-y-4">
          {Object.entries(speciesGroups).sort().map(([species, vars]) => (
            <div key={species} className="bg-white rounded-lg shadow">
              <div className="px-4 py-2 bg-gray-50 border-b rounded-t-lg">
                <h3 className="font-medium text-sm">{species} ({vars.length})</h3>
              </div>
              <div className="divide-y">
                {vars.map((v) => (
                  <div key={v.id} className="p-3 flex items-center justify-between hover:bg-gray-50">
                    <div>
                      <span className="font-medium text-sm">{v.name}</span>
                      {v.is_hybrid && <span className="text-xs ml-2 bg-blue-100 text-blue-700 px-1.5 rounded">F1</span>}
                      {v.needs_tunnel && <span className="text-xs ml-1 bg-orange-100 text-orange-700 px-1.5 rounded">Tunnel</span>}
                      <p className="text-xs text-gray-500 mt-0.5">
                        {v.plant_family?.name}
                        {v.days_to_maturity && ` · ${v.days_to_maturity}j`}
                        {v.product && ` · Lie a: ${v.product.name}`}
                      </p>
                    </div>
                    <button onClick={() => openVarietyModal(v)} className="text-gray-400 hover:text-gray-600 text-sm">✏️</button>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {varieties.length === 0 && (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-400">
              Aucune variete enregistree
            </div>
          )}
        </div>
      )}

      {/* Seeds Tab */}
      {tab === 'seeds' && (
        <div className="bg-white rounded-lg shadow">
          {seedLots.length === 0 ? (
            <div className="p-8 text-center text-gray-400">Aucun lot de semences</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Variete</th>
                  <th className="text-left px-4 py-2 font-medium">Fournisseur</th>
                  <th className="text-right px-4 py-2 font-medium">Restant</th>
                  <th className="text-left px-4 py-2 font-medium">Unite</th>
                  <th className="text-left px-4 py-2 font-medium">Source</th>
                  <th className="text-left px-4 py-2 font-medium">Bio</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {seedLots.map((lot) => {
                  const pct = Number(lot.initial_quantity) > 0 ? (Number(lot.remaining_quantity) / Number(lot.initial_quantity)) * 100 : 0;
                  return (
                    <tr key={lot.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2">
                        <span className="font-medium">{lot.variety?.species}</span>
                        <span className="text-gray-500 ml-1">{lot.variety?.name}</span>
                      </td>
                      <td className="px-4 py-2 text-gray-600">{lot.supplier_name || '-'}</td>
                      <td className="px-4 py-2 text-right">
                        <span className={pct < 20 ? 'text-red-600 font-medium' : ''}>
                          {lot.remaining_quantity}
                        </span>
                        <span className="text-gray-400"> / {lot.initial_quantity}</span>
                      </td>
                      <td className="px-4 py-2 text-gray-600">{lot.unit}</td>
                      <td className="px-4 py-2">
                        <span className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
                          {SEED_SOURCE_LABELS[lot.source] || lot.source}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        {lot.is_certified_bio ? '✅' : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Variety Modal */}
      {varietyModalOpen && (
        <Modal title={editingVariety ? 'Modifier la variete' : 'Nouvelle variete'} onClose={() => setVarietyModalOpen(false)}>
          <div className="space-y-3">
            <select className="input" value={varietyForm.plant_family_id} onChange={(e) => setVarietyForm({ ...varietyForm, plant_family_id: e.target.value })}>
              <option value="">Famille botanique</option>
              {families.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
            <div className="grid grid-cols-2 gap-3">
              <input className="input" placeholder="Espece (ex: Tomate)" value={varietyForm.species} onChange={(e) => setVarietyForm({ ...varietyForm, species: e.target.value })} />
              <input className="input" placeholder="Variete (ex: Maestria F1)" value={varietyForm.name} onChange={(e) => setVarietyForm({ ...varietyForm, name: e.target.value })} />
            </div>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={varietyForm.is_hybrid} onChange={(e) => setVarietyForm({ ...varietyForm, is_hybrid: e.target.checked })} />
                Hybride F1
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={varietyForm.needs_tunnel} onChange={(e) => setVarietyForm({ ...varietyForm, needs_tunnel: e.target.checked })} />
                Tunnel necessaire
              </label>
            </div>
            <p className="text-xs font-medium text-gray-500 mt-2">Cycle cultural (optionnel)</p>
            <div className="grid grid-cols-3 gap-2">
              <input className="input text-xs" type="number" placeholder="Semis debut (sem.)" value={varietyForm.seeding_start_week} onChange={(e) => setVarietyForm({ ...varietyForm, seeding_start_week: e.target.value })} />
              <input className="input text-xs" type="number" placeholder="Semis fin (sem.)" value={varietyForm.seeding_end_week} onChange={(e) => setVarietyForm({ ...varietyForm, seeding_end_week: e.target.value })} />
              <input className="input text-xs" type="number" placeholder="Repiquage (sem.)" value={varietyForm.transplant_week_offset} onChange={(e) => setVarietyForm({ ...varietyForm, transplant_week_offset: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input className="input text-xs" type="number" placeholder="Jours maturite" value={varietyForm.days_to_maturity} onChange={(e) => setVarietyForm({ ...varietyForm, days_to_maturity: e.target.value })} />
              <input className="input text-xs" type="number" placeholder="Duree recolte (sem.)" value={varietyForm.harvest_duration_weeks} onChange={(e) => setVarietyForm({ ...varietyForm, harvest_duration_weeks: e.target.value })} />
            </div>
            <textarea className="input" rows={2} placeholder="Notes" value={varietyForm.notes} onChange={(e) => setVarietyForm({ ...varietyForm, notes: e.target.value })} />
            <div className="flex justify-end gap-2">
              <button onClick={() => setVarietyModalOpen(false)} className="px-4 py-2 text-sm text-gray-600">Annuler</button>
              <button onClick={saveVariety} className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700">Enregistrer</button>
            </div>
          </div>
        </Modal>
      )}

      {/* SeedLot Modal */}
      {seedModalOpen && (
        <Modal title="Nouveau lot de semences" onClose={() => setSeedModalOpen(false)}>
          <div className="space-y-3">
            <select className="input" value={seedForm.variety_id} onChange={(e) => setSeedForm({ ...seedForm, variety_id: e.target.value })}>
              <option value="">Choisir une variete</option>
              {varieties.map((v) => <option key={v.id} value={v.id}>{v.species} - {v.name}</option>)}
            </select>
            <div className="grid grid-cols-2 gap-3">
              <select className="input" value={seedForm.source} onChange={(e) => setSeedForm({ ...seedForm, source: e.target.value })}>
                {Object.entries(SEED_SOURCE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <input className="input" placeholder="Fournisseur" value={seedForm.supplier_name} onChange={(e) => setSeedForm({ ...seedForm, supplier_name: e.target.value })} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <input className="input" type="number" placeholder="Quantite" value={seedForm.initial_quantity} onChange={(e) => setSeedForm({ ...seedForm, initial_quantity: e.target.value })} />
              <select className="input" value={seedForm.unit} onChange={(e) => setSeedForm({ ...seedForm, unit: e.target.value })}>
                <option value="graines">graines</option>
                <option value="g">g</option>
                <option value="kg">kg</option>
                <option value="GN">GN</option>
                <option value="GE">GE</option>
                <option value="MGN">MGN</option>
              </select>
              <input className="input" type="number" step="0.01" placeholder="Prix total (EUR)" value={seedForm.price_total_cents} onChange={(e) => setSeedForm({ ...seedForm, price_total_cents: e.target.value })} />
            </div>
            <input className="input" placeholder="N. de lot fournisseur" value={seedForm.lot_number} onChange={(e) => setSeedForm({ ...seedForm, lot_number: e.target.value })} />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={seedForm.is_certified_bio} onChange={(e) => setSeedForm({ ...seedForm, is_certified_bio: e.target.checked })} />
              Certifie bio
            </label>
            <div className="flex justify-end gap-2">
              <button onClick={() => setSeedModalOpen(false)} className="px-4 py-2 text-sm text-gray-600">Annuler</button>
              <button onClick={saveSeedLot} className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700">Enregistrer</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold mb-4">{title}</h2>
        {children}
      </div>
    </div>
  );
}
